import React, { FunctionComponent, memo, useEffect, useMemo } from 'react'
import styles from './RequestTaskRow.module.sass'
import { EMPTY_TASK, PartialTask, Task } from '../../../../../responses/tasks'
import { flow } from 'fp-ts/es6/function'
import { parseDate, toDateString, toServerDate } from '../../../../../utils/date-utils'
import Ellipsis from '../../../../etc/Ellipsis/Ellipsis'
import Switch from '../../../../controls/Switch/Switch'
import { TenderStatus } from '../../../../data-components/pickers/TenderPicker/TenderPicker'
import { apiRequests } from '../../../../../http/config'
import { getId } from '../../../../../utils/entity-utils'
import DateInput from '../../../../data-components/date/DateInput/DateInput'
import PriorityPicker from '../../../../data-components/pickers/PriorityPicker/PriorityPicker'
import Button, { ButtonStyle } from '../../../../controls/Button/Button'
import Tooltip from 'react-tooltip-lite'
import { TaskStatus } from '../../../../../constants/enums'
import { Link } from 'react-router-dom'
import { urlWithParams } from '../../../../../http/utils'
import { ROUTES } from '../../../../../http/routes'
import { HasAllPermissions } from '../../../../etc/HasPermission'
import { PERMISSIONS } from '../../../../../constants/permissions'
import Html from '../../../../etc/Html/Html'
import { ID } from '../../../../../types/UtilityTypes'
import { objectSelector, projectSelector, taskSelector } from '../../../../../utils/selectors'
import { useRootSelector } from '../../../../../utils/hooks'
import UserPicker from '../../../../data-components/pickers/UserPicker/UserPicker'
import { EMPTY_PROJECT } from '../../../../../responses/projects'
import {fromQueryWithReset, ResetSearch} from '../../../../../utils/rx-operators'
import ObjectName from '../../../../etc/ObjectName/ObjectName'
import DeleteIcon from 'files/delete.svg'
import CommentIcon from 'files/comment/comment.svg'
import { useQueries } from '../../../../../utils/hooks/useQueries'
import { TaskLine } from '../../../../data-components/UserTasks/UserTasks'
import ChevronRightIcon from 'files/chevron/right.svg'
import { useSetState } from 'react-use'
import { Colors } from '../../../../../styles'
import { useObservableState } from 'observable-hooks'
//import { User } from '../../../../../responses/users'
import Dropdown from '../../../../data-components/dropdowns/Dropdown/Dropdown'
import { unit } from '../../../../../utils/utils'
import {Observable, of} from 'rxjs'
import {switchMap} from 'rxjs/operators'

interface Props {
    taskId: ID,
    onChange: (changes: PartialTask) => void,
    onDelete: (task: Task) => void,
}

const RequestTaskRow: FunctionComponent<Props> = props => {
    const original = useRootSelector(taskSelector(props.taskId)) || EMPTY_TASK
    const object = useRootSelector(objectSelector(original.project_work_object_id))
    const project = useRootSelector(projectSelector(object?.project_id)) || EMPTY_PROJECT
    const [task, setTask] = useSetState<Partial<Task>>(original)
    useEffect(() => setTask(original), [original, setTask])

    const url = useMemo(() => urlWithParams({ taskId: task.id })(ROUTES.TASK), [task])
    const {isMobile} = useQueries()

    const getUsers = (data: Observable<{ query: ResetSearch, taskTypeID?: ID }>) => data.pipe(
        switchMap(({query, taskTypeID}) => of(query).pipe(
            fromQueryWithReset(query => apiRequests.project.targetUsersNewTask({
                params: { projectId: project.id }, query: { ...query, task_type: taskTypeID }
            }))
        ))
    )

    const [users, onUserSearch] = useObservableState(getUsers, [])

    /*const [users, onUserSearch] = useObservableState<User[], {search: string, reset: boolean}>(fromQueryWithReset(
        query => apiRequests.project.targetUsers({ params: { projectId: project.id }, query }))
        , []
    )*/

    const [types, searchType] = useObservableState(fromQueryWithReset(
        query => apiRequests.dictionary.list({ params: { key: 'task-type' }, query })
    ), [])

    return <div className={styles.row}>
        {
            isMobile ? <TaskLine task={original}>
                <ChevronRightIcon/>
            </TaskLine>: <>
                <Link to={url} className={styles.code}>
                    <Tooltip content={project.name}>
                        {project.code}
                    </Tooltip>
                </Link>
                <Link to={url} className={styles.date}>
                    <Tooltip content={'Дата создания'}>{flow(parseDate, toDateString)(task.details?.created_at)}</Tooltip>
                </Link>
                <Link to={url} className={styles.name}><ObjectName object={object}/></Link>

                <Link to={url} className={styles.user}>
                    <Tooltip content={'Создатель запроса'}><Ellipsis>{task.details?.user?.name}</Ellipsis></Tooltip>
                </Link>
                <Link to={url} className={styles.comment}>
                    {
                        task.details?.comment && <Tooltip
                            content={<Html html={task.details.comment?.text}/>}
                        >
                            <CommentIcon/>
                        </Tooltip>
                    }
                </Link>
                <div><Switch
                    value={task.tender_id !== TenderStatus.CANCELED}
                    onChange={value => setTask({
                        tender_id: value ?
                            TenderStatus.IN_WORK :
                            TenderStatus.CANCELED
                    })}
                    text={'Тендер'}
                /></div>
                <Dropdown
                    className={styles.type}
                    placeholder={'Тип задач'}
                    content={(selected, render) => selected.map(render).join()}
                    data={types}
                    toKey={getId}
                    selected={unit(task.type)}
                    render={type => type.name}
                    onClick={type => setTask({type})}
                    onChange={(search, reset) => searchType({search, reset})}
                />
                <div className={styles.user_picker}>
                    <UserPicker
                        minimal
                        data={users}
                        text={'Добавить исполнителя'}
                        title={'Изменить исполнителя'}
                        user={task.target_user}
                        onClick={target_user => setTask({ target_user })}
                        //onChange={(search, reset) => onUserSearch({search, reset})}
                        onChange={(search, reset) => {
                            onUserSearch({query: {search, reset}, taskTypeID: task.type?.id})
                        }}
                    />
                </div>
                <div><DateInput
                    placeholder={'Дедлайн'}
                    disabledDays={{before: new Date()}}
                    value={parseDate(task.deadline_at)}
                    onChange={date => setTask({ deadline_at: toServerDate(date) })}
                /></div>
                <div className={styles.priority}>
                    <PriorityPicker
                        priority={task.priority_id ?? EMPTY_TASK.priority_id}
                        onChange={priority_id => setTask({ priority_id })}
                    />
                </div>

                <div>
                    <HasAllPermissions permissions={[PERMISSIONS.TASK.OPEN]}>
                        <Button
                            color={Colors.blue}
                            disabled={!task.deadline_at}
                            buttonStyle={ButtonStyle.BORDERED}
                            title={!task.deadline_at ? 'Не задан дедлайн' : undefined}
                            onClick={() => props.onChange({ ...task, status_id: TaskStatus.OPENED })}
                        >
                            Открыть
                        </Button>
                    </HasAllPermissions>
                </div>
                <div>
                    <HasAllPermissions permissions={[PERMISSIONS.TASK.DELETE]}>
                        <Button onClick={() => props.onDelete(original)}><DeleteIcon/></Button>
                    </HasAllPermissions>
                </div>
            </>
        }
    </div>
}

export default memo(RequestTaskRow)